// Ildstøtte / kill-chain — Call for Fire (CFF), 9-Line CAS, mållista,
// fareavstand (danger close) + fratricide-varsel, og våpenrekkevidde-ringer
// (WEZ / threat rings). Rene, testbare byggere/kalkyler — ingen infrastruktur.
//
// ÆRLIGHET: rekkevidde- og fareavstands-tall er PLANLEGGINGSTALL (åpne kilder /
// grov doktrine). De erstatter IKKE JFIRE/GTA-tabellene eller ballistisk
// beregning — de gir situasjonsforståelse og en «declare DANGER CLOSE»-terskel.
import { z } from 'zod';
import { haversineM, ringPolygon } from './geo.js';
// ── Våpenkategorier + doktrine-tall ─────────────────────────────────────────
export const FireWeapon = z.enum(['mortar', 'artillery', 'naval-gunfire', 'rockets', 'cas']);
/** Terskel (m) der «DANGER CLOSE» må erklæres i CFF (planleggingstall). */
export const DANGER_CLOSE_M = {
    mortar: 600,
    artillery: 600,
    'naval-gunfire': 750,
    rockets: 2000,
    cas: 500,
};
export const FIRE_WEAPON_LABEL = {
    mortar: 'Bombekaster',
    artillery: 'Artilleri',
    'naval-gunfire': 'Sjømålsild',
    rockets: 'Rakettartilleri (MLRS)',
    cas: 'Nærstøtte (CAS)',
};
/** Åpne-kilde planleggings-rekkevidder for trussel-ringer (WEZ). */
export const THREAT_SYSTEMS = [
    { id: 'small-arms', name: 'Håndvåpen', rangeM: 500 },
    { id: 'atgm', name: 'Panservern (ATGM)', rangeM: 4000 },
    { id: 'manpads', name: 'MANPADS (IR-luftvern)', rangeM: 6000 },
    { id: 'mortar-120', name: 'Bombekaster 120 mm', rangeM: 7000 },
    { id: 'shorad', name: 'Kortholds-luftvern', rangeM: 15000 },
    { id: 'artillery-152', name: 'Artilleri 152 mm', rangeM: 24000 },
];
// ── Call for Fire (CFF) ─────────────────────────────────────────────────────
export const FireMissionType = z.enum(['adjust-fire', 'fire-for-effect', 'suppress', 'immediate-suppression']);
export const FireControl = z.enum(['at-my-command', 'when-ready', 'time-on-target', 'continuous']);
export const CallForFireSchema = z.object({
    id: z.string().max(64),
    /** Observatør-kallesignal. */
    observer: z.string().max(64),
    missionType: FireMissionType.default('adjust-fire'),
    /** Målets posisjon. */
    targetLat: z.number().min(-90).max(90),
    targetLon: z.number().min(-180).max(180),
    /** Fritekst-nett/MGRS for menneskelig lesning (valgfri). */
    targetGrid: z.string().max(32).optional(),
    targetDesc: z.string().max(500),
    /** Ammunisjon/effekt (fritekst, f.eks. «HE i overflatebrann»). */
    ammunition: z.string().max(200).default('HE'),
    control: FireControl.default('when-ready'),
    weapon: FireWeapon.default('artillery'),
    dangerClose: z.boolean().default(false),
    /** Kommandantens initialer — kreves ved danger close. */
    authInitials: z.string().max(16).optional(),
    tsMs: z.number(),
});
const MISSION_LABEL = {
    'adjust-fire': 'JUSTER ILD',
    'fire-for-effect': 'ILD FOR EFFEKT',
    suppress: 'NØYTRALISER',
    'immediate-suppression': 'ØYEBLIKKELIG NØYTRALISERING',
};
const CONTROL_LABEL = {
    'at-my-command': 'PÅ MIN KOMMANDO',
    'when-ready': 'NÅR KLAR',
    'time-on-target': 'TID PÅ MÅL',
    continuous: 'KONTINUERLIG ILD',
};
/** Standard CFF-brief (tre transmisjoner), lesbar for tale/logg. */
export function buildCffText(c) {
    const grid = c.targetGrid ?? `${c.targetLat.toFixed(5)}, ${c.targetLon.toFixed(5)}`;
    const lines = [
        // 1) Identifikasjon + oppdragstype
        `${c.observer} — ILDANMODNING. ${MISSION_LABEL[c.missionType]}.`,
        // 2) Målplassering
        `MÅL: ${grid}${c.dangerClose ? '  ⚠ DANGER CLOSE' : ''}.`,
        // 3) Målbeskrivelse + effekt + kontroll
        `BESKRIVELSE: ${c.targetDesc}.`,
        `AMMUNISJON: ${c.ammunition}. KONTROLL: ${CONTROL_LABEL[c.control]}.`,
    ];
    // Ekte autorisasjonsgate: DANGER CLOSE krever at en kommandant faktisk har
    // oppgitt initialer. Uten det står brief-en som UTESTÅENDE — den skal aldri
    // auto-stemples som autorisert (fratricide-kontroll).
    if (c.dangerClose) {
        lines.push(c.authInitials
            ? `DANGER CLOSE — AUTORISERT AV: ${c.authInitials}.`
            : `DANGER CLOSE — AUTORISASJON UTESTÅENDE (krever kommandant-godkjenning før ild).`);
    }
    return lines.join('\n');
}
// ── 9-Line CAS ──────────────────────────────────────────────────────────────
export const NineLineCasSchema = z.object({
    id: z.string().max(64),
    ipHp: z.string().max(120).default(''), // 1: IP/BP (kontrollpunkt)
    heading: z.string().max(60).default(''), // 2: kurs (IP→mål) + offset
    distanceM: z.number().min(0).max(200000).default(0), // 3: avstand
    targetElevM: z.number().default(0), // 4: målhøyde (m)
    targetDesc: z.string().max(300).default(''), // 5: målbeskrivelse
    targetLat: z.number().min(-90).max(90), // 6: målplassering
    targetLon: z.number().min(-180).max(180),
    targetGrid: z.string().max(32).optional(),
    markType: z.string().max(120).default(''), // 7: markering av mål
    friendlies: z.string().max(200).default(''), // 8: nærmeste egne
    egress: z.string().max(120).default(''), // 9: retrett
    remarks: z.string().max(500).default(''),
    tsMs: z.number(),
});
const NINE_LINE_LABELS = [
    'Linje 1 — IP/BP',
    'Linje 2 — Kurs (offset)',
    'Linje 3 — Avstand',
    'Linje 4 — Målhøyde',
    'Linje 5 — Målbeskrivelse',
    'Linje 6 — Målplassering',
    'Linje 7 — Markering',
    'Linje 8 — Egne styrker',
    'Linje 9 — Retrett',
];
/** Formatert 9-line CAS-brief til tale/logg. */
export function buildNineLineText(n) {
    const grid = n.targetGrid ?? `${n.targetLat.toFixed(5)}, ${n.targetLon.toFixed(5)}`;
    const vals = [
        n.ipHp,
        n.heading,
        `${Math.round(n.distanceM)} m`,
        `${Math.round(n.targetElevM)} m`,
        n.targetDesc,
        grid,
        n.markType,
        n.friendlies,
        n.egress,
    ];
    const body = NINE_LINE_LABELS.map((l, i) => `${l}: ${vals[i] || '—'}`).join('\n');
    return n.remarks ? `${body}\nMerknader: ${n.remarks}` : body;
}
// ── Fire mission-status (kill-chain lifecycle) ──────────────────────────────
export const FireMissionStatus = z.enum([
    'requested', // anmodet
    'ready', // klar (batteriet meldt klart)
    'shot', // skudd avfyrt
    'splash', // nedslag om få sekunder
    'rounds-complete', // ild fullført
    'end-of-mission', // oppdrag avsluttet
    'check-fire', // stans ilden
    'cancelled', // avlyst
]);
export const FIRE_STATUS_LABEL = {
    requested: 'Anmodet',
    ready: 'Klar',
    shot: 'Skudd',
    splash: 'Splash',
    'rounds-complete': 'Ild fullført',
    'end-of-mission': 'Oppdrag avsluttet',
    'check-fire': 'CHECK FIRE',
    cancelled: 'Avlyst',
};
/** Normal fremdrift i kill-chain. check-fire/cancelled kan skje når som helst. */
const NEXT = {
    requested: 'ready',
    ready: 'shot',
    shot: 'splash',
    splash: 'rounds-complete',
    'rounds-complete': 'end-of-mission',
};
/** Neste status i normal sekvens (null om ingen). */
export function nextFireStatus(s) {
    return NEXT[s] ?? null;
}
// ── Mållista (Target List) ──────────────────────────────────────────────────
export const FireTargetPriority = z.enum(['routine', 'priority', 'hpt', 'tst']); // TST = time-sensitive
export const FireTargetSchema = z.object({
    id: z.string().max(64),
    /** Mål-nummer (f.eks. AB1001), som i doktrine. */
    number: z.string().max(24),
    desc: z.string().max(300),
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    priority: FireTargetPriority.default('routine'),
    status: FireMissionStatus.default('requested'),
    tsMs: z.number(),
});
export const FIRE_TARGET_PRIORITY_LABEL = {
    routine: 'Rutine',
    priority: 'Prioritert',
    hpt: 'HPT (høyverdi)',
    tst: 'TST (tidskritisk)',
};
/** Sorter mållista: TST → HPT → prioritert → rutine, nyeste først innen nivå. */
export function sortFireTargets(ts) {
    const rank = { tst: 0, hpt: 1, priority: 2, routine: 3 };
    return [...ts].sort((a, b) => rank[a.priority] - rank[b.priority] || b.tsMs - a.tsMs);
}
/** Sjekk om egne styrker ligger innenfor fareavstanden for et ildmål. */
export function dangerCloseCheck(target, friendlies, weapon) {
    // Fail-safe: ukjent våpen → bruk STØRSTE kjente fareavstand (varsler heller
    // for mye enn for lite; en fratricide-sjekk må aldri feile «åpen»/stille).
    const radiusM = DANGER_CLOSE_M[weapon] ?? Math.max(...Object.values(DANGER_CLOSE_M));
    const within = [];
    for (const f of friendlies) {
        const d = haversineM(target, { lat: f.lat, lon: f.lon });
        if (d <= radiusM)
            within.push({ friendly: f, distanceM: d });
    }
    within.sort((a, b) => a.distanceM - b.distanceM);
    return { radiusM, within, declareDangerClose: within.length > 0 };
}
// ── Våpenrekkevidde-ringer (WEZ / threat rings) ─────────────────────────────
/** Ring-polygon ([lon,lat]) for et våpens/trusselsystems rekkevidde. */
export function engagementRing(center, radiusM) {
    return ringPolygon(center, Math.max(1, radiusM));
}
/** Fareavstands-ring for et ildmål (til visning før skudd). */
export function dangerCloseRing(center, weapon) {
    return ringPolygon(center, DANGER_CLOSE_M[weapon]);
}
// ── CDE (Collateral Damage Estimate — utilsiktet skade) ─────────────────────
// Forenklet planleggings-CDE: en fare-radius per våpen mot BESKYTTEDE OBJEKTER
// (sivile, sykehus, skoler, kulturminne). ÆRLIG avgrensning: dette er et
// beslutnings-STØTTE-verktøy, ikke en formell CDE-metodologi (CDM/JMEM).
/** Utilsiktet-skade-radius (m) per våpen — planleggingstall, > fareavstand. */
export const CDE_RADIUS_M = {
    mortar: 800,
    artillery: 1000,
    'naval-gunfire': 1200,
    rockets: 2500,
    cas: 700,
};
export const CDE_LEVEL_LABEL = {
    ingen: 'Ingen', lav: 'Lav', moderat: 'Moderat', hoy: 'Høy',
};
export const CDE_LEVEL_COLOR = {
    ingen: '#4bd15b', lav: '#ffd24a', moderat: '#ff8a3d', hoy: '#ff3b3b',
};
/** Vurder utilsiktet skade mot beskyttede objekter for et ildmål.
 * Nivå: <25 % av radius → høy, <50 % → moderat, innenfor → lav, ellers ingen. */
export function cdeCheck(target, sites, weapon) {
    const radiusM = CDE_RADIUS_M[weapon] ?? Math.max(...Object.values(CDE_RADIUS_M));
    const within = [];
    for (const s of sites) {
        const d = haversineM(target, { lat: s.lat, lon: s.lon });
        if (d <= radiusM)
            within.push({ site: s, distanceM: d });
    }
    within.sort((a, b) => a.distanceM - b.distanceM);
    const nearest = within[0] ?? null;
    let level = 'ingen';
    if (nearest) {
        const frac = nearest.distanceM / radiusM;
        level = frac < 0.25 ? 'hoy' : frac < 0.5 ? 'moderat' : 'lav';
    }
    return { radiusM, within, level, nearest };
}
/** CDE-ring for visning før skudd. */
export function cdeRing(center, weapon) {
    return ringPolygon(center, CDE_RADIUS_M[weapon]);
}
// ── BDA (Battle Damage Assessment — skadevurdering etter skudd) ──────────────
export const BdaEffect = z.enum(['destroyed', 'neutralized', 'suppressed', 'no-effect', 'unknown']);
export const BDA_EFFECT_LABEL = {
    destroyed: 'Ødelagt', neutralized: 'Nøytralisert', suppressed: 'Nedkjempet (midlertidig)',
    'no-effect': 'Ingen effekt', unknown: 'Ukjent',
};
export const BdaSchema = z.object({
    id: z.string().max(64),
    targetId: z.string().max(64),
    effect: BdaEffect.default('unknown'),
    /** Anslått nøytraliserings-% (0–100). */
    percent: z.number().min(0).max(100).default(0),
    assessedBy: z.string().max(64).default(''),
    tsMs: z.number().default(0),
    note: z.string().max(2000).default(''),
});
export function parseBda(raw) {
    const p = BdaSchema.safeParse(raw);
    return p.success ? p.data : null;
}
/** Krever re-angrep? (ingen/utilstrekkelig effekt). */
export function bdaNeedsReattack(b) {
    return b.effect === 'no-effect' || (b.effect === 'suppressed' && b.percent < 50) || (b.effect === 'unknown');
}
/** Kort sammendrag til liste. */
export function bdaSummary(b) {
    return `${BDA_EFFECT_LABEL[b.effect]}${b.percent ? ` · ${b.percent}%` : ''}${bdaNeedsReattack(b) ? ' · RE-ANGREP?' : ''}`;
}
//# sourceMappingURL=firesupport.js.map